import { coordinationEnabledForVersion } from '@arcexperts/core-types'
import { defineStore } from 'pinia'
import { type DefaultObject } from '@/types'
import { useClientStore } from '@/stores/client'
import { useContentStore } from '@/stores/content'
import type { UpdateDataType } from '@/components/partials/display/VersionUpdate.vue'
import { BuildingPlan, type RoleTypes } from '@/schemas/general/enums'
import {
  migrateStandardUsageCategories,
  normalizeUsageBuildingTypeSelections,
} from '@/helpers/building-type-availability'
import { getVersionLevel, hasStoredResults, type VersionLevel } from '@/helpers/version'
import {
  buildStoredServiceResultsV2,
  hasLegacyStoredServiceResults,
  type StoredServiceResultsV2,
  type PercentageRangeByPhase,
  type LegacyStoredServiceHours,
} from '@/helpers/service-results'
import {
  useProjectArchitectCalculator,
  useProjectLandscapeArchitectCalculator,
  useProjectElectricalCalculator,
  useProjectEngineerCalculator,
  useProjectHVACCalculator,
  useProjectSanitaryCalculator,
} from '@/stores/projectCenter/projectServiceCalculatorFactory'
import { DEFAULT_COMPLEXITY_PERCENTAGE } from '@arcexperts/core-types'
import * as Sentry from '@sentry/vue'
import { deepClone } from '@/helpers/objects_arrays'
import i18n from '@/i18n'
import { validateLanguage } from '@/helpers/shared'
import { ROLE_PART_PREFIX } from '@/helpers/project-roles'
import { mirrorProject } from '@/services/projectMirror'
import { migrateQualityFactorSegments, qualitySegmentValues } from '@/helpers/quality-segments'
import { useCalculatorForRole, type Role } from '@/stores/projectCenter/useCalculatorForRole'
import type { UsageCorrection } from '@/schemas/general/types'

export interface EnrichedUpdate {
  update: UpdateDataType
  level: VersionLevel | null
  hasStoredResults: boolean
}

export interface State {
  defaultProject: DefaultObject | null
  project: DefaultObject | null
  projectMeta: DefaultObject | null
  availableUpdates: UpdateDataType[] | null
  enrichedUpdates: EnrichedUpdate[] | null
  canPersistClientSave: boolean
  useStoredResults: boolean
  activeProjectRole: string | null
  /** Incremented when the whole project is replaced (import); pages keyed on it remount. */
  projectRevision: number
}

let saveTimer: ReturnType<typeof setTimeout> | null = null
const GENERAL_USAGE_TASKS_MIGRATION_VERSION = '1.1.0'
const GENERAL_INTERVENTION_SLIDERS_MIGRATION_VERSION = '1.2.0'
const STANDARD_USAGE_CATEGORIES_MIGRATION_VERSION = '1.1.0'
const GENERAL_INTERVENTION_SLIDER_MIN = 0
const GENERAL_INTERVENTION_SLIDER_MAX = 100
const GENERAL_INTERVENTION_SLIDER_STEP = 10
const GENERAL_INTERVENTION_SLIDER_FIELDS = [
  'rawConstruction',
  'facadeEnvelope',
  'interiors',
  'electricInstallation',
  'heatingVentilation',
  'sanitary',
] as const
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

// crypto.randomUUID is only available in secure contexts; local *.lo domains
// run on plain HTTP, so fall back to building a v4 UUID from getRandomValues.
function createUUID(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function ensureMirrorProjectId(project: DefaultObject): string {
  const widgetMeta = (project.widgetMeta as Record<string, unknown> | undefined) ?? {}
  const existingProjectId = widgetMeta.projectId

  if (typeof existingProjectId === 'string' && UUID_PATTERN.test(existingProjectId)) {
    return existingProjectId
  }

  const projectId = createUUID()
  project.widgetMeta = {
    ...widgetMeta,
    projectId,
  }
  return projectId
}

function compareSemver(a: string | undefined, b: string | undefined): number {
  const parse = (version: string | undefined): [number, number, number] => {
    const [maj, min, pat] = (version ?? '')
      .split('.')
      .map((v) => Number(v))
      .map((v) => (Number.isFinite(v) ? v : 0))
    return [maj ?? 0, min ?? 0, pat ?? 0]
  }

  const [aMaj, aMin, aPat] = parse(a)
  const [bMaj, bMin, bPat] = parse(b)

  if (aMaj !== bMaj) return aMaj - bMaj
  if (aMin !== bMin) return aMin - bMin
  return aPat - bPat
}

function shouldRunGeneralUsageTasksMigration(currentVersion: string | undefined, nextVersion: string): boolean {
  return (
    compareSemver(currentVersion, GENERAL_USAGE_TASKS_MIGRATION_VERSION) < 0 &&
    compareSemver(nextVersion, GENERAL_USAGE_TASKS_MIGRATION_VERSION) >= 0
  )
}

function shouldRunGeneralInterventionSlidersMigration(
  currentVersion: string | undefined,
  nextVersion: string
): boolean {
  return (
    compareSemver(currentVersion, GENERAL_INTERVENTION_SLIDERS_MIGRATION_VERSION) < 0 &&
    compareSemver(nextVersion, GENERAL_INTERVENTION_SLIDERS_MIGRATION_VERSION) >= 0
  )
}

function shouldRunStandardUsageCategoriesMigration(currentVersion: string | undefined, nextVersion: string): boolean {
  return (
    compareSemver(currentVersion, STANDARD_USAGE_CATEGORIES_MIGRATION_VERSION) < 0 &&
    compareSemver(nextVersion, STANDARD_USAGE_CATEGORIES_MIGRATION_VERSION) >= 0
  )
}

function hasAcceptedStandardUsageCategoriesMigration(project: DefaultObject): boolean {
  return Object.entries(project).some(([part, value]) => {
    if (!part.endsWith('_setup') || part === 'landscape_architect_setup' || !value) return false
    const version = (value as Record<string, unknown>).version as string | undefined
    return compareSemver(version, STANDARD_USAGE_CATEGORIES_MIGRATION_VERSION) >= 0
  })
}

function normalizeGeneralInterventionSliderValue(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  const clamped = Math.min(GENERAL_INTERVENTION_SLIDER_MAX, Math.max(GENERAL_INTERVENTION_SLIDER_MIN, value))
  return Math.round(clamped / GENERAL_INTERVENTION_SLIDER_STEP) * GENERAL_INTERVENTION_SLIDER_STEP
}

function cleanupLegacyGeneralTaskFields(
  generalPart: Record<string, unknown> | undefined,
  options?: { clearPerUsageFactorResults?: boolean }
): boolean {
  if (!generalPart) return false

  const generalData = generalPart.data as Record<string, unknown> | undefined
  if (!generalData) return false

  const existingCalculatedResults = generalPart.calculatedResults as Record<string, unknown> | undefined
  const existingResults = existingCalculatedResults?.results as Record<string, unknown> | undefined

  let changed = false

  const hasLegacyTopLevelTaskFields =
    'buildingPlan' in generalData ||
    'rawConstruction' in generalData ||
    'facadeEnvelope' in generalData ||
    'interiors' in generalData ||
    'electricInstallation' in generalData ||
    'heatingVentilation' in generalData ||
    'sanitary' in generalData
  if (hasLegacyTopLevelTaskFields) {
    delete generalData.buildingPlan
    delete generalData.rawConstruction
    delete generalData.facadeEnvelope
    delete generalData.interiors
    delete generalData.electricInstallation
    delete generalData.heatingVentilation
    delete generalData.sanitary
    changed = true
  }

  if (existingCalculatedResults && existingResults) {
    const hadLegacyScalarFactorKeys =
      'buildingPlanCostFactor' in existingResults || 'buildingPlanServiceFactor' in existingResults

    if (hadLegacyScalarFactorKeys) {
      delete existingResults.buildingPlanCostFactor
      delete existingResults.buildingPlanServiceFactor
      changed = true
    }

    if (options?.clearPerUsageFactorResults) {
      const hadPerUsageFactorKeys =
        'buildingPlanCostFactorsForUsages' in existingResults ||
        'buildingPlanServiceFactorsForUsages' in existingResults
      delete existingResults.buildingPlanCostFactorsForUsages
      delete existingResults.buildingPlanServiceFactorsForUsages
      if (hadPerUsageFactorKeys) {
        changed = true
      }
    }
  }

  return changed
}

function migrateGeneralUsageTasksOnVersionUpdate(generalPart: Record<string, unknown> | undefined): boolean {
  if (!generalPart) return false

  const generalData = generalPart.data as Record<string, unknown> | undefined
  if (!generalData) return false

  const usageItems = generalData.usageItems as Record<string, unknown>[] | undefined
  if (!Array.isArray(usageItems) || usageItems.length === 0) return false

  const legacyBuildingPlan = (generalData.buildingPlan as BuildingPlan | undefined) ?? BuildingPlan.NEW_BUILDING
  const legacyRawConstruction = (generalData.rawConstruction as number | undefined) ?? DEFAULT_COMPLEXITY_PERCENTAGE
  const legacyFacadeEnvelope = (generalData.facadeEnvelope as number | undefined) ?? legacyRawConstruction
  const legacyInteriors = (generalData.interiors as number | undefined) ?? DEFAULT_COMPLEXITY_PERCENTAGE
  const legacyElectricInstallation =
    (generalData.electricInstallation as number | undefined) ?? DEFAULT_COMPLEXITY_PERCENTAGE
  const legacyHeatingVentilation =
    (generalData.heatingVentilation as number | undefined) ?? DEFAULT_COMPLEXITY_PERCENTAGE
  const legacySanitary = (generalData.sanitary as number | undefined) ?? DEFAULT_COMPLEXITY_PERCENTAGE

  let changed = false

  const normalized = usageItems.map((usage) => {
    const next = {
      ...usage,
      // Explicit migration on version update: old General task was global, so copy it to every usage.
      buildingPlan: legacyBuildingPlan,
      rawConstruction: legacyRawConstruction,
      facadeEnvelope: legacyFacadeEnvelope,
      interiors: legacyInteriors,
      electricInstallation: legacyElectricInstallation,
      heatingVentilation: legacyHeatingVentilation,
      sanitary: legacySanitary,
    }

    if (
      usage.buildingPlan !== next.buildingPlan ||
      usage.rawConstruction !== next.rawConstruction ||
      usage.facadeEnvelope !== next.facadeEnvelope ||
      usage.interiors !== next.interiors ||
      usage.electricInstallation !== next.electricInstallation ||
      usage.heatingVentilation !== next.heatingVentilation ||
      usage.sanitary !== next.sanitary
    ) {
      changed = true
    }

    return next
  })

  if (changed) {
    generalData.usageItems = normalized
  }
  return cleanupLegacyGeneralTaskFields(generalPart, { clearPerUsageFactorResults: true }) || changed
}

/**
 * Moves the quality adjustment factor of a setup part onto the segments of the current step
 * size (closest segment; factor overrides become the overall slider). Needs the step size
 * from the socket; legacy final min/m² values are only converted when the base rate is known.
 */
function migrateSetupQualityFactorSegments(part: string, setupPart: Record<string, unknown> | undefined): boolean {
  const stepSize = useContentStore().getComplexityStepSize(part)
  const corrections = (setupPart?.data as { usageCorrections?: UsageCorrection[] } | undefined)?.usageCorrections
  if (stepSize === undefined || !corrections?.length) return false
  const role = part.slice(0, -'_setup'.length).toUpperCase() as Role
  const calculator = useCalculatorForRole(role).value
  return migrateQualityFactorSegments(corrections, qualitySegmentValues(stepSize), (index) =>
    calculator.getMinutesPerSquareMeterForUsageAtIndexIgnoringManualComplexity(index)
  )
}

function normalizeGeneralInterventionSliders(generalPart: Record<string, unknown> | undefined): boolean {
  if (!generalPart) return false

  const generalData = generalPart.data as Record<string, unknown> | undefined
  if (!generalData) return false

  const usageItems = generalData.usageItems as Record<string, unknown>[] | undefined
  if (!Array.isArray(usageItems) || usageItems.length === 0) return false

  let changed = false

  const normalized = usageItems.map((usage) => {
    const nextUsage = { ...usage }

    GENERAL_INTERVENTION_SLIDER_FIELDS.forEach((field) => {
      const currentValue = usage[field] as unknown
      const normalizedValue = normalizeGeneralInterventionSliderValue(currentValue)
      if (normalizedValue === null || currentValue === normalizedValue) {
        return
      }

      nextUsage[field] = normalizedValue
      changed = true
    })

    return nextUsage
  })

  if (changed) {
    generalData.usageItems = normalized
  }

  return cleanupLegacyGeneralTaskFields(generalPart, { clearPerUsageFactorResults: true }) || changed
}

function getSetupPartKeyForServicePart(servicePart: string): string | null {
  if (!servicePart.endsWith('_service')) return null
  return servicePart.replace(/_service$/, '_setup')
}

function buildStoredServiceResultsForPart(
  project: DefaultObject,
  servicePartKey: string
): StoredServiceResultsV2 | null {
  const setupPartKey = getSetupPartKeyForServicePart(servicePartKey)
  if (!setupPartKey) return null

  const servicePart = project[servicePartKey] as Record<string, unknown> | undefined
  const setupPart = project[setupPartKey] as Record<string, unknown> | undefined
  const serviceResults = (servicePart?.calculatedResults as { results?: Record<string, unknown> } | undefined)?.results
  const setupResults = (setupPart?.calculatedResults as { results?: Record<string, unknown> } | undefined)?.results
  const phases =
    ((servicePart?.data as { phases?: unknown[] } | undefined)?.phases as unknown[] | undefined) ??
    (serviceResults?.defaultPhases as unknown[] | undefined) ??
    []

  if (!Array.isArray(phases) || !serviceResults || !setupResults) return null

  return buildStoredServiceResultsV2({
    estimatedTotalHours: (setupResults.estimatedTotalHours as number) ?? 0,
    phases: deepClone(phases) as Parameters<typeof buildStoredServiceResultsV2>[0]['phases'],
    percentageRangeForPhases: (serviceResults.percentageRangeForPhases as PercentageRangeByPhase | undefined) ?? {},
    serviceHours: (serviceResults.serviceHours as LegacyStoredServiceHours | undefined) ?? {},
  })
}

function getLiveServiceResultsForPart(part: string): {
  estimatedTotalHours: number
  phases: Parameters<typeof buildStoredServiceResultsV2>[0]['phases']
  percentageRangeForPhases: PercentageRangeByPhase
  serviceHours: LegacyStoredServiceHours | null
} | null {
  switch (part) {
    case 'architect_service': {
      const calculator = useProjectArchitectCalculator()
      return {
        estimatedTotalHours: calculator.estimatedTotalHours,
        phases: deepClone(calculator.serviceData?.phases ?? []),
        percentageRangeForPhases: calculator.percentageRangeForPhases,
        serviceHours: calculator.serviceHours,
      }
    }
    case 'landscape_architect_service': {
      const calculator = useProjectLandscapeArchitectCalculator()
      return {
        estimatedTotalHours: calculator.estimatedTotalHours,
        phases: deepClone(calculator.serviceData?.phases ?? []),
        percentageRangeForPhases: calculator.percentageRangeForPhases,
        serviceHours: calculator.serviceHours,
      }
    }
    case 'engineer_service': {
      const calculator = useProjectEngineerCalculator()
      return {
        estimatedTotalHours: calculator.estimatedTotalHours,
        phases: deepClone(calculator.serviceData?.phases ?? []),
        percentageRangeForPhases: calculator.percentageRangeForPhases,
        serviceHours: calculator.serviceHours,
      }
    }
    case 'electrical_service': {
      const calculator = useProjectElectricalCalculator()
      return {
        estimatedTotalHours: calculator.estimatedTotalHours,
        phases: deepClone(calculator.serviceData?.phases ?? []),
        percentageRangeForPhases: calculator.percentageRangeForPhases,
        serviceHours: calculator.serviceHours,
      }
    }
    case 'hvac_service': {
      const calculator = useProjectHVACCalculator()
      return {
        estimatedTotalHours: calculator.estimatedTotalHours,
        phases: deepClone(calculator.serviceData?.phases ?? []),
        percentageRangeForPhases: calculator.percentageRangeForPhases,
        serviceHours: calculator.serviceHours,
      }
    }
    case 'sanitary_service': {
      const calculator = useProjectSanitaryCalculator()
      return {
        estimatedTotalHours: calculator.estimatedTotalHours,
        phases: deepClone(calculator.serviceData?.phases ?? []),
        percentageRangeForPhases: calculator.percentageRangeForPhases,
        serviceHours: calculator.serviceHours,
      }
    }
    default:
      return null
  }
}

export const useProjectStore = defineStore('project', {
  state: (): State => ({
    project: null,
    projectMeta: null,
    defaultProject: null,
    availableUpdates: null,
    enrichedUpdates: null,
    canPersistClientSave: false,
    useStoredResults: false,
    activeProjectRole: null,
    projectRevision: 0,
  }),
  getters: {
    getProject: (state): DefaultObject | null => state.project,
    getMeta: (state): DefaultObject | null => state.projectMeta,
    getActiveProjectRole: (state): string | null => state.activeProjectRole,
    getValidRoles: (state): Array<string> => {
      // a role can still be assigned to a user, but the part has been removed from the project.
      const validRoles: Array<string> = []
      state.projectMeta?.roles.forEach((role: RoleTypes) => {
        if (state.projectMeta?.parts.includes(role)) {
          validRoles.push(role)
        }
      })
      return validRoles
    },
    getVersion: (state) => (part: string) =>
      state.project && state.project[part] ? state.project[part].version : null,
    getAvailableUpdates: (state) => state.availableUpdates,
    getEnrichedUpdates: (state) => state.enrichedUpdates,
    getHighestUpdateLevel: (state): VersionLevel | null => {
      if (!state.enrichedUpdates?.length) return null
      const levels: VersionLevel[] = ['patch', 'minor', 'major']
      let highest = -1
      for (const eu of state.enrichedUpdates) {
        if (eu.level) {
          const idx = levels.indexOf(eu.level)
          if (idx > highest) highest = idx
        }
      }
      return highest >= 0 ? levels[highest] : null
    },
  },
  actions: {
    async initProject(data: { project?: DefaultObject; meta?: DefaultObject }) {
      const project = data?.project ?? null
      const projectMeta = data?.meta ?? null
      const currentLanguage = validateLanguage(i18n.global.locale.value)
      if (projectMeta && !projectMeta.language && currentLanguage) {
        projectMeta.language = currentLanguage
      }

      if (
        project?.general &&
        compareSemver(project.general.version as string | undefined, GENERAL_USAGE_TASKS_MIGRATION_VERSION) >= 0
      ) {
        const cleaned = cleanupLegacyGeneralTaskFields(project.general as Record<string, unknown>)
        if (cleaned) {
          project.general.updated = Date.now()
        }
      }

      if (
        project?.general &&
        compareSemver(project.general.version as string | undefined, GENERAL_INTERVENTION_SLIDERS_MIGRATION_VERSION) >=
          0
      ) {
        const normalized = normalizeGeneralInterventionSliders(project.general as Record<string, unknown>)
        if (normalized) {
          project.general.updated = Date.now()
        }
      }

      if (project) {
        for (const [partKey, partValue] of Object.entries(project)) {
          if (!partKey.endsWith('_service') || !partValue) continue

          const servicePart = partValue as Record<string, unknown>
          const calculatedResults = servicePart.calculatedResults as Record<string, unknown> | undefined
          const results = calculatedResults?.results

          if (!hasLegacyStoredServiceResults(results)) continue

          const migratedResults = buildStoredServiceResultsForPart(project, partKey)
          if (!migratedResults) continue

          calculatedResults!.results = migratedResults as unknown as Record<string, unknown>
          calculatedResults!.calculatedAt = (calculatedResults!.calculatedAt as number) || Date.now()
          calculatedResults!.parameterVersion = (servicePart.version as string | undefined) ?? ''
          servicePart.updated = Date.now()
        }
      }

      // Backfill project-level lastCalculatedAt from per-part values
      if (project && !project.lastCalculatedAt) {
        let latest = 0
        for (const partValue of Object.values(project)) {
          const calcAt = (partValue as Record<string, unknown> | undefined)?.calculatedResults as
            | Record<string, unknown>
            | undefined
          const timestamp = calcAt?.calculatedAt as number | undefined
          if (timestamp && timestamp > latest) latest = timestamp
        }
        if (latest) {
          project.lastCalculatedAt = latest
        }
      }

      this.$patch({
        canPersistClientSave: false,
        project,
        projectMeta,
        activeProjectRole: null,
      })
      useClientStore().registerMetaUserId(typeof projectMeta?.userId === 'string' ? projectMeta.userId : null)
      Sentry.setContext('project', data?.project ?? null)
      Sentry.setContext('project_meta', projectMeta)

      this.normalizeGeneralBuildingTypes({ save: false })
    },

    async updateProjectPart(part: string, data: Record<string, unknown>) {
      const project = this.getProject
      if (!project || !project[part]) {
        return false
      }
      const now = Date.now()
      project[part].data = data
      project[part].updated = now

      if (part === 'general') {
        this.normalizeGeneralBuildingTypes({ save: false })
      }

      if (part.endsWith('_service') && Array.isArray(data.phases)) {
        const liveServiceResults = getLiveServiceResultsForPart(part)
        if (liveServiceResults?.serviceHours) {
          const storedResults = buildStoredServiceResultsV2(liveServiceResults)
          this.replaceCalculatedResults(part, storedResults, { save: false })
        }
      }

      this.$patch({ project: project })
      return true
    },

    setActiveProjectRole(role: string) {
      this.activeProjectRole = role
    },

    /**
     * Replaces the loaded project with an imported one. Metadata stays as is,
     * the usual load-time migrations run, and the result is saved to the host.
     */
    async applyImportedProject(project: DefaultObject, options?: { updates?: UpdateDataType[] }) {
      const meta = this.projectMeta ? { ...this.projectMeta } : undefined
      const widgetMeta = (this.project?.widgetMeta as Record<string, unknown> | undefined) ?? {}
      const imported = deepClone(project) as DefaultObject
      imported.widgetMeta = { ...widgetMeta, ...((imported.widgetMeta as Record<string, unknown> | undefined) ?? {}) }

      // $patch deep-merges plain objects, so keys missing in the loaded state
      // would survive inside existing parts; drop the old project first.
      this.project = null
      await this.initProject({ project: imported, meta })
      this.$patch({
        availableUpdates: null,
        enrichedUpdates: null,
        useStoredResults: false,
      })

      // Saved states may predate the current parameters: run the automatic
      // patch/minor migrations (the same ones "accept update" runs) once here.
      for (const update of options?.updates ?? []) {
        this.versionUpdate(update.part, update.version, { save: false })
      }

      this.$patch({ projectRevision: this.projectRevision + 1 })
      this.persistClientSave()
    },

    normalizeGeneralBuildingTypes(options?: { save?: boolean }): boolean {
      const project = this.getProject
      const generalPart = project?.general as Record<string, unknown> | undefined
      const generalData = generalPart?.data as Record<string, unknown> | undefined
      const usageItems = generalData?.usageItems as
        | Array<{ type?: string | null; buildingTypeId?: string | null; buildingTypeName?: string | null }>
        | undefined

      if (!project || !generalPart || !generalData || !Array.isArray(usageItems) || usageItems.length === 0) {
        return false
      }

      const contentStore = useContentStore()
      const migrated = hasAcceptedStandardUsageCategoriesMigration(project)
        ? migrateStandardUsageCategories(usageItems, contentStore.getBuildingTypes)
        : { changed: false, usageItems }
      const normalized = normalizeUsageBuildingTypeSelections(
        migrated.usageItems,
        generalData.country,
        contentStore.getBuildingTypes
      )

      if (!migrated.changed && !normalized.changed) {
        return false
      }

      generalData.usageItems = normalized.usageItems as unknown[]
      generalPart.updated = Date.now()
      this.$patch({ project })

      if (options?.save ?? true) {
        this.scheduleClientSave()
      }

      return true
    },

    scheduleClientSave() {
      if (saveTimer) clearTimeout(saveTimer)
      saveTimer = setTimeout(() => {
        saveTimer = null
        this.triggerClientSave()
      }, 500)
    },

    triggerClientSave() {
      if (saveTimer) {
        clearTimeout(saveTimer)
        saveTimer = null
      }
      if (!this.canPersistClientSave) return
      const projectData = this.getProject
      const clientStore = useClientStore()
      const userId = this.projectMeta?.userId
      const refreshToken = clientStore.refreshToken
      const canMirror =
        projectData &&
        typeof userId === 'string' &&
        userId.length > 0 &&
        typeof refreshToken === 'string' &&
        refreshToken.length > 0
      const mirrorProjectId = canMirror ? ensureMirrorProjectId(projectData) : null
      const language = typeof this.projectMeta?.language === 'string' ? this.projectMeta.language : undefined
      const projectDataToSave =
        projectData && language
          ? {
              ...projectData,
              widgetMeta: {
                ...((projectData.widgetMeta as Record<string, unknown> | undefined) ?? {}),
                language,
              },
            }
          : projectData
      const user = clientStore.getUser
      if (user && user.origin) {
        window.parent.postMessage({ type: 'on_save', data: JSON.stringify(projectDataToSave) }, user.origin)
      }

      if (
        mirrorProjectId &&
        canMirror &&
        typeof userId === 'string' &&
        this.projectMeta &&
        projectDataToSave &&
        refreshToken
      ) {
        void mirrorProject(
          {
            projectId: mirrorProjectId,
            userId,
            meta: this.projectMeta,
            project: projectDataToSave,
          },
          refreshToken
        ).catch((error: unknown) => {
          Sentry.captureException(error, {
            tags: { operation: 'project_mirror' },
            extra: { projectId: mirrorProjectId },
          })
        })
      }
    },

    persistClientSave() {
      this.canPersistClientSave = true
      this.triggerClientSave()
    },

    updateProjectLanguage(language: string) {
      const meta = this.projectMeta
      if (!meta || meta.language === language) {
        return
      }

      this.projectMeta = { ...meta, language }
      if (this.project) {
        this.project.widgetMeta = {
          ...((this.project.widgetMeta as Record<string, unknown> | undefined) ?? {}),
          language,
        }
      }
      this.persistClientSave()
    },

    updateCalculatedResults(part: string, key: string, value: unknown) {
      const project = this.getProject
      if (!project || !project[part]) return

      if (!project[part].calculatedResults) {
        project[part].calculatedResults = {
          calculatedAt: Date.now(),
          parameterVersion: project[part].version ?? '',
          results: {},
        }
      }

      project[part].calculatedResults.results[key] = value
      const now = Date.now()
      project[part].calculatedResults.calculatedAt = now
      project[part].calculatedResults.parameterVersion = project[part].version ?? ''
      project.lastCalculatedAt = now

      this.$patch({ project })
      this.scheduleClientSave()
    },

    replaceCalculatedResults(
      part: string,
      results: Record<string, unknown> | StoredServiceResultsV2,
      options?: { save?: boolean }
    ) {
      const project = this.getProject
      if (!project || !project[part]) return

      if (!project[part].calculatedResults) {
        project[part].calculatedResults = {
          calculatedAt: Date.now(),
          parameterVersion: project[part].version ?? '',
          results: {},
        }
      }

      project[part].calculatedResults.results = deepClone(results) as Record<string, unknown>
      const now = Date.now()
      project[part].calculatedResults.calculatedAt = now
      project[part].calculatedResults.parameterVersion = project[part].version ?? ''
      project.lastCalculatedAt = now

      this.$patch({ project })

      if (options?.save !== false) {
        this.scheduleClientSave()
      }
    },

    setLastDownloadedAt() {
      const project = this.getProject
      if (!project) return
      project.lastDownloadedAt = Date.now()
      this.$patch({ project })
      this.persistClientSave()
    },

    ensureProjectId(): string | null {
      const project = this.getProject
      if (!project) return null
      const widgetMeta = (project.widgetMeta as Record<string, unknown> | undefined) ?? {}
      const hadProjectId = typeof widgetMeta.projectId === 'string' && UUID_PATTERN.test(widgetMeta.projectId)
      const projectId = ensureMirrorProjectId(project)
      if (!hadProjectId) {
        this.$patch({ project })
        // Force-persist: canPersistClientSave is false after loading a project
        // and a guarded scheduleClientSave would silently drop the new id,
        // orphaning every PDF that gets uploaded under it.
        this.persistClientSave()
      }
      return projectId
    },

    setAvailableUpdates(updates: UpdateDataType[]) {
      const allowedUpdates: UpdateDataType[] = []
      updates.forEach((update) => {
        if (update.part === 'general' && this.getMeta?.canEdit) {
          allowedUpdates.push(update)
          return
        }
        for (const [role, prefix] of Object.entries(ROLE_PART_PREFIX)) {
          if ([`${prefix}_setup`, `${prefix}_service`].includes(update.part) && this.getMeta?.roles.includes(role)) {
            allowedUpdates.push(update)
            return
          }
        }
      })

      const project = this.getProject
      const enriched: EnrichedUpdate[] = allowedUpdates.map((update) => {
        const part = project?.[update.part]
        const currentVersion = part?.version as string | undefined
        const level = currentVersion ? getVersionLevel(currentVersion, update.version) : null
        return {
          update,
          level,
          hasStoredResults: part ? hasStoredResults(part as Record<string, unknown>) : false,
        }
      })

      this.$patch({
        availableUpdates: allowedUpdates.length ? allowedUpdates : null,
        enrichedUpdates: enriched.length ? enriched : null,
      })
    },

    setUseStoredResults(value: boolean) {
      this.$patch({ useStoredResults: value })
    },

    versionUpdate(part: string, version: string, options?: { save?: boolean }) {
      const project = this.getProject
      if (project && project[part]) {
        const currentVersion = project[part].version as string | undefined

        const shouldMigrateStandardUsageCategories =
          part.endsWith('_setup') &&
          part !== 'landscape_architect_setup' &&
          shouldRunStandardUsageCategoriesMigration(currentVersion, version)

        if (part === 'general') {
          if (shouldRunGeneralUsageTasksMigration(currentVersion, version)) {
            migrateGeneralUsageTasksOnVersionUpdate(project.general as Record<string, unknown> | undefined)
          }

          if (shouldRunGeneralInterventionSlidersMigration(currentVersion, version)) {
            normalizeGeneralInterventionSliders(project.general as Record<string, unknown> | undefined)
          }
        }

        if (part.endsWith('_setup')) {
          migrateSetupQualityFactorSegments(part, project[part] as Record<string, unknown>)
        }

        if (part === 'specialist_coordination_setup') {
          const settings = useContentStore().coordinationSettings
          if (
            !coordinationEnabledForVersion(currentVersion, settings) &&
            coordinationEnabledForVersion(version, settings)
          ) {
            project[part].data.coordinationSurchargePercent = settings!.defaultPercent
            project[part].data.coordinationExcludedRoles = []
            project[part].data.coordinationJustification = ''
          }
        }
        project[part].version = version
        project[part].updated = Date.now()
        this.$patch({ project: project })
        if (shouldMigrateStandardUsageCategories) {
          this.normalizeGeneralBuildingTypes({ save: false })
        }
        if (options?.save !== false) {
          this.persistClientSave()
        }
        const availableUpdates = this.getAvailableUpdates
        const updates = availableUpdates?.filter((update) => update.part !== part)
        const enriched = this.enrichedUpdates?.filter((eu) => eu.update.part !== part)
        this.$patch({
          availableUpdates: updates?.length ? updates : null,
          enrichedUpdates: enriched?.length ? enriched : null,
        })
      }
    },
    versionIgnore(part: string, version: string) {
      const project = this.getProject
      if (project && project[part]) {
        project[part].ignored_version = version
        project[part].updated = Date.now()
        this.$patch({ project: project })
        this.persistClientSave()
        const availableUpdates = this.getAvailableUpdates
        const updates = availableUpdates?.filter((update) => update.part === part)
        const enriched = this.enrichedUpdates?.filter((eu) => eu.update.part === part)
        this.$patch({
          availableUpdates: updates?.length ? updates : null,
          enrichedUpdates: enriched?.length ? enriched : null,
        })
      }
    },
  },
})
